import { getConfig, getCurrencyFormatter, imagePathResolver, NumberFormats as nf, type Currency } from '@shared/utils';
import { getTranslations } from 'next-intl/server';
import Image from 'next/image';
import { BackChevron } from '../../BackChevron/BackChevron';
import { ContentDetailsGroup } from '../../ContentDetailsGroup/ContentDetailsGroup';
import type { ProductVertical } from '../../PageToolbarWithNavigation/navigationConfig/types';
import { RichContent } from '../../RichContent/RichContent';
import { SecondaryHeaderClient } from '../../SecondaryHeaderContainer/SecondaryHeaderContainer';
import { GameProductJsonLd } from '../GameProductJsonLd/GameProductJsonLd';
import { GameResponsiveActions } from './components/GameResponsiveActions/GameResponsiveActions';
import type { GameInfoType } from './types';

type GameDescriptionProps = {
  description: string | undefined;
  className?: string;
};

function GameDescription({ description, className }: GameDescriptionProps) {
  return (
    <div className={className}>
      <p className="paragraph text-foreground-subtle">{description}</p>
    </div>
  );
}

type GameInfoPageContentProps = {
  game: GameInfoType;
  gameName: string;
  productVertical: ProductVertical;
  currency: Currency;
};

async function GameInfoPageContent({ game, gameName, productVertical, currency }: GameInfoPageContentProps) {
  const t = await getTranslations('casino');
  const currencyFormatter = await getCurrencyFormatter();
  const { baseImgUrl, baseUrl } = getConfig();

  const pods = [
    game?.provider && {
      label: t('game_info.provider'),
      info: Array.isArray(game.provider) ? game.provider.join(', ') : game.provider,
    },
    game?.minimumStake != null && {
      label: t('game_info.min_bet'),
      info: currencyFormatter(game.minimumStake)
        .format(nf.WITH_DECIMALS, value => value)
        .get(),
    },
    game?.volatility && {
      label: t('game_info.volatility'),
      info: game.volatility.join(', '),
    },
  ].filter((pod): pod is { label: string; info: string } => Boolean(pod));

  return (
    <div
      data-testid="game-info-container"
      data-track-container="Game Info"
      data-track-game-id={gameName}
      data-track-game-name={gameName}
      data-track-game-provider={game?.provider}
      data-track-game-volatility={game?.volatility?.join(', ')}
      data-track-game-minimum-stake={currencyFormatter(game?.minimumStake ?? 0)
        .format(nf.WITH_DECIMALS, value => value)
        .get()}
      data-track-name-callout-show={'no'}
      data-track-name-callout={0}
    >
      <GameProductJsonLd game={game} gameName={gameName} productVertical={productVertical} currency={currency} baseUrl={baseUrl} />
      <SecondaryHeaderClient className="ll-container-full py-3">
        <div className="flex items-center gap-2 ll-container-inner">
          <BackChevron data-track-name="Back" headerVariant="subheader" />
          <h1 className="heading-5" data-testid="game-info-title" data-track-name={gameName}>
            {game.name}
          </h1>
        </div>
        <GameDescription description={game?.content} className="hidden md:flex items-center justify-between ll-container-inner pl-10" />
      </SecondaryHeaderClient>

      <GameDescription description={game?.content} className="ll-container-full md:hidden px-6 mb-6" />
      <div className="ll-narrow-grid-container">
        <div className="ll-container-inner">
          {game.image && (
            <Image
              src={imagePathResolver(game.image, baseImgUrl, true)}
              priority={true}
              alt={game.name}
              width={480}
              height={270}
              className="w-full h-auto md:mt-10 mb-3"
              sizes="(max-width: 768px) 100vw, 480px"
            />
          )}
          <GameResponsiveActions id={gameName} game={game} playButtonText={t('game_info.play_game')} productVertical={productVertical} />
          {pods.length > 0 && <ContentDetailsGroup columns={2} pods={pods} className="mb-6" />}
          {game?.instructions && (
            <>
              <p className="paragraph-strong mb-4">{t('game_info.details_title')}</p>
              <RichContent information={game.instructions} className="mb-6" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { GameInfoPageContent };
